<script setup>
import { ref } from 'vue'
import Commons from '@/shared/utils/commons'
import TableList from '../others/TableList.vue'
import SlideDetailView from '../others/SlideDetailView.vue'
import DealDetails from '../deals/DealDetails.vue'
import DealForm from '../deals/DealForm.vue'
import ActionTrigger from '@/features/data/presentation/components/ActionTrigger.vue'
import { EVENTS } from '@/shared/constants/commons'
import Events from '@/events/events'

const detailData = ref(null)
const editDeal = ref(false)
const saveDealTrigger = ref(null)

const buttons = [
  // {
  //   title: '&plus; Nouveau Deal',
  //   onClick() {
  //     detailData.value = { mode: 'create' }
  //     editDeal.value = true
  //   },
  // },
 ]

const cols = [
  {
    name: 'Cible & Éléments',
    id: 'target',
    value(el) {
      let typeLabel = ''
      let items = []

      if (el.targetType === 'category') {
        typeLabel = 'Catégorie'
      } else if (el.targetType === 'dish') {
        typeLabel = 'Plat'
      } else if (el.targetType === 'delivery') {
        typeLabel = 'Livraison'
      } else {
        typeLabel = el.targetType
      }

      // targetData est maintenant un tableau d'objets avec une propriété 'name'
      const targetNames = (el.targetData || []).map(t => t.name).filter(Boolean)

      const itemsHtml = targetNames.length > 0
        ? `<div class="mt-1 flex flex-wrap gap-1 max-w-[200px]">` + targetNames.map(name => `<span class="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px] whitespace-nowrap overflow-hidden text-ellipsis max-w-full">${name}</span>`).join('') + `</div>`
        : ''

      return `
        <div>
          <span class="font-medium text-gray-700 text-sm">${typeLabel}</span>
          ${itemsHtml}
        </div>
      `
    },
  },
  {
    name: 'Restaurant',
    id: 'storeName',
    value(el) {
      return [
        el.storeName || 'Global',
      ]
    },
  },
  {
    name: 'Réduction',
    id: 'value',
    value(el) {
      const val = el.discountType === 'percent' ? `-${el.discountValue}%` : `-${Commons.formatNumber(el.discountValue, { suffix: ' F' })}`
      return `<span class="font-black text-primary-600 bg-primary-50 px-2 py-1 rounded-lg text-sm">${val}</span>`
    },
  },
  {
    name: 'Période',
    id: 'validity',
    value(el) {
      const start = el.startDate ? Commons.time(el.startDate, 'DD MMM') : '??'
      const end = el.endDate ? Commons.time(el.endDate, 'DD MMM YYYY') : '??'
      return `
        <div class="text-xs">
          <p class="font-medium text-gray-700">Jusqu'au ${end}</p>
          <p class="text-[10px] text-gray-400">Début: ${start}</p>
        </div>
      `
    },
  },
  {
    name: 'Statut',
    id: 'status',
    value(el) {
      const isActive = el.status === 'active'
      const colorClass = isActive ? 'text-success-600 bg-success-50' : 'text-gray-400 bg-gray-50'
      const label = isActive ? 'Actif' : 'Inactif'
      return `<span class="${colorClass} text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full border border-current opacity-80">${label}</span>`
    },
  },
]

const onEditDeal = () => {
  detailData.value = { ...detailData.value, mode: 'edit' }
  editDeal.value = true
}

const confirmDeleteDeal = async deal => {
  const res = await window.customPrompt({
    title: `Voulez-vous vraiment supprimer ce deal ?`,
    description: 'Cette action est irréversible.',
    formFields: [],
    successText: 'Supprimer',
    cancelText: 'Annuler',
  })

  if (res.accepted) {
    try {
      await saveDealTrigger.value.trigger({
        action: 'delete',
        deal: { id: deal.id },
      })

      Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
        title: 'Succès',
        body: 'Le deal a été supprimé',
      })

      detailData.value = null
    } catch (error) {
      Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
        title: 'Erreur',
        body: 'Impossible de supprimer le deal',
      })
    }
  }
}

const onDealSaved = async payload => {
  try {
    await saveDealTrigger.value.trigger(payload)

    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Succès',
      body: 'Le deal a été enregistré',
    })

    editDeal.value = false
    detailData.value = null
  } catch (error) {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Erreur',
      body: 'Erreur lors de l\'enregistrement',
    })
  }
}

const onDealError = error => {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Erreur',
    body: error.result?.message || "Erreur lors de l'enregistrement",
  })
}
</script>

<template>
  <div class="p-5 h-full overflow-auto bg-gray-50">
    <div class="mb-8 flex justify-between items-end">
      <div>
        <h1 class="text-3xl font-black text-gray-900 mb-1 tracking-tight">Promotions</h1>
        <p class="text-gray-500 text-sm">Gérez les codes de réduction et les offres promotionnelles</p>
      </div>
    </div>
    
    <div class="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden">
      <TableList
        title="Liste des Deals"
        dataKey="deals"
        hasSearch
        :cols="cols"
        :buttons="buttons"
        :refreshOnEvents="['DEAL_UPDATED']"
      ></TableList>
    </div>

    <!-- Deal Details -->
    <SlideDetailView :isVisible="!!detailData && !editDeal" @hide="detailData = null">
      <DealDetails
        v-if="detailData"
        :dealData="detailData"
        @edit="onEditDeal"
        @delete="confirmDeleteDeal(detailData)"
      ></DealDetails>
    </SlideDetailView>

    <!-- Deal Form -->
    <SlideDetailView
      :isVisible="!!detailData && editDeal"
      @hide="editDeal = false; detailData = null"
    >
      <DealForm
        v-if="detailData"
        :dealData="detailData"
        @saved="onDealSaved"
        @cancel="editDeal = false; detailData = null"
      />
    </SlideDetailView>

    <!-- Action Trigger -->
    <ActionTrigger
      dataKey="saveDeal"
      ref="saveDealTrigger"
      @success="() => {
        Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
          title: 'Succès',
          body: 'Le deal a été enregistré',
        })
      }"
      @failed="onDealError"
      dispatchOnSuccess="DEAL_UPDATED"
    />
  </div>
</template>
